import React from 'react';
import { Alert, Box, Button, CircularProgress, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../../services/firebase';
import { AUTH_STORAGE_KEYS } from '../../services/auth/constants';
import { fetchMedicationOccurrence, recordMedicationRound } from '../../services/medicationReminderService';
import { useRole } from '../../contexts/RoleContext';
import { isMedicationAdministrationResolved, MEDICATION_NOT_TAKEN_REASONS } from '../../features/medicationReminders/medicationAdministrationOptions';
import { buildMedicationTimelineNavigationState } from '../../features/medicationReminders/medicationTimelineNavigation';
const localDateTimeValue = (date = new Date()) => new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

const MedicationRoundPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [user, authLoading] = useAuthState(auth);
  const { childrenWithAccess, loading: roleLoading, getUserRoleForChild } = useRole();
  const ids = React.useMemo(() => (params.get('occurrences') || '').split(',').filter(Boolean).slice(0, 25), [params]);
  const childId = params.get('childId') || '';
  const [occurrences, setOccurrences] = React.useState([]);
  const [decisions, setDecisions] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const returnTo = `${window.location.pathname}${window.location.search}`;
      localStorage.setItem(AUTH_STORAGE_KEYS.RETURN_URL, returnTo); sessionStorage.setItem(AUTH_STORAGE_KEYS.RETURN_URL, returnTo);
      navigate('/login', { replace: true, state: { returnTo } }); return;
    }
    Promise.all(ids.map(fetchMedicationOccurrence)).then((items) => {
      const available = items.filter(Boolean);
      setOccurrences(available);
      setDecisions(Object.fromEntries(available.map((item) => [item.id, {
        status: isMedicationAdministrationResolved(item.status) ? item.status : 'taken', reason: item.reason || '', note: item.note || '',
        allowCorrection: isMedicationAdministrationResolved(item.status),
        actualTakenAt: localDateTimeValue(item.actualTakenAt || item.recordedAt || new Date()),
      }])));
      setLoading(false);
    }).catch(() => { setError('This medication round is not available.'); setLoading(false); });
  }, [authLoading, ids, navigate, user]);

  const child = childrenWithAccess.find((item) => item.id === childId);
  const canEdit = ['care_owner', 'care_partner'].includes(getUserRoleForChild(childId));
  const update = (id, patch) => setDecisions((current) => ({ ...current, [id]: { ...current[id], ...patch } }));
  const confirm = async () => {
    setSaving(true); setError('');
    try {
      const isCorrection = occurrences.some((item) => isMedicationAdministrationResolved(item.status));
      await recordMedicationRound(occurrences.map((item) => ({ occurrenceId: item.id, ...decisions[item.id] })));
      navigate('/dashboard', {
        replace: true,
        state: buildMedicationTimelineNavigationState({
          childId,
          occurrenceIds: occurrences.map((item) => item.id),
          scheduledDayKey: occurrences[0]?.scheduledDayKey,
          updated: isCorrection,
        }),
      });
    } catch (nextError) { setError(nextError?.message || 'The medication round could not be saved. Nothing was changed.'); }
    finally { setSaving(false); }
  };

  if (authLoading || roleLoading || loading) return <Box sx={{ minHeight: '70dvh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  if (!child || !occurrences.length) return <Box sx={{ maxWidth: 650, mx: 'auto', p: 2 }}><Alert severity="info">This medication round is no longer available.</Alert><Button sx={{ mt: 2 }} onClick={() => navigate('/home')}>Done</Button></Box>;

  return <Box sx={{ maxWidth: 650, mx: 'auto', px: 2, py: 3 }}>
    <Button onClick={() => navigate('/home')}>Done</Button>
    <Typography variant="overline" color="primary.main" sx={{ fontWeight: 900 }}>{child.name} · medication round</Typography>
    <Typography variant="h4" sx={{ fontWeight: 900 }}>Medications due</Typography>
    <Typography color="text.secondary">Confirm the whole list, or change only the doses that were missed or not taken.</Typography>
    {error ? <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert> : null}
    <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />} sx={{ mt: 2 }}>
      {occurrences.map((occurrence) => {
        const medication = occurrence.medicationSnapshot || {}; const decision = decisions[occurrence.id] || {};
        const dose = [medication.dose, medication.unit].filter(Boolean).join(' ');
        return <Box key={occurrence.id} sx={{ py: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 850 }}>{medication.name || 'Medication'}</Typography>
          <Typography color="text.secondary">{dose || 'Dose not listed'} · {occurrence.scheduledTime}</Typography>
          <ToggleButtonGroup exclusive fullWidth value={decision.status} onChange={(_, value) => value && update(occurrence.id, { status: value })} sx={{ mt: 1 }} disabled={!canEdit || saving}>
            <ToggleButton value="taken">Taken</ToggleButton><ToggleButton value="missed">Missed</ToggleButton><ToggleButton value="not_taken">Not taken</ToggleButton>
          </ToggleButtonGroup>
          {decision.status === 'not_taken' ? <Stack spacing={1} sx={{ mt: 1 }}>
            <TextField select size="small" label="Reason" value={decision.reason} onChange={(event) => update(occurrence.id, { reason: event.target.value })}>{MEDICATION_NOT_TAKEN_REASONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField>
            <TextField size="small" label="Note (optional)" value={decision.note} onChange={(event) => update(occurrence.id, { note: event.target.value })} />
          </Stack> : null}
          {decision.status === 'taken' ? <TextField fullWidth size="small" sx={{ mt: 1 }} label="Actual time taken" type="datetime-local" value={decision.actualTakenAt} onChange={(event) => update(occurrence.id, { actualTakenAt: event.target.value })} InputLabelProps={{ shrink: true }} /> : null}
        </Box>;
      })}
    </Stack>
    {canEdit ? <Button fullWidth size="large" variant="contained" disabled={saving || Object.values(decisions).some((item) => item.status === 'not_taken' && !item.reason)} onClick={confirm} sx={{ mt: 2 }}>Confirm all {occurrences.length} medications</Button> : <Alert severity="info" sx={{ mt: 2 }}>A Care Owner or Care Partner can edit this round.</Alert>}
  </Box>;
};

export default MedicationRoundPage;
